import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useToast } from '../../../../../components/common/Toast';
import { useAccessTheme } from '../useAccessTheme';
import AddIdentityProviderModal from './AddIdentityProviderModal';
import IdentityProviderCard from './IdentityProviderCard';
import EntraSetupModal from './EntraSetupModal';
import ActiveDirectorySetupModal from './ActiveDirectorySetupModal';
import OktaSetupModal from './OktaSetupModal';
import OidcSetupModal from './OidcSetupModal';
import SamlSetupModal from './SamlSetupModal';
import ScimSetupModal from './ScimSetupModal';
import ConfirmDialog from '../../../Dialogs/ConfirmDialog';
import {
  disconnectEntraDirectory,
  listIdentityProviders,
  syncEntraDirectory,
  type IdentityProviderListItem,
} from './entraConnectionService';
import {
  DEMO_SETUP_MESSAGE,
  formatRelativeSync,
  type FeatureBadge,
  type IdentityProviderKind,
  type IdentityProviderRecord,
} from './identityProviderTypes';

interface IdentityProviderSectionProps {
  addModalOpen: boolean;
  onOpenAddModal: () => void;
  onCloseAddModal: () => void;
  showHeader?: boolean;
}

function mapListedProvider(item: IdentityProviderListItem): IdentityProviderRecord {
  return {
    id: item.connectionId || item.id,
    kind: 'entra',
    name: item.name || 'Microsoft Entra ID',
    status: item.status,
    secondaryLabel: item.organization || undefined,
    userCount: item.userCount,
    groupCount: item.groupCount,
    autoSync: item.autoSync,
    lastSync: formatRelativeSync(item.lastSyncAt),
    ssoEnabled: item.ssoEnabled,
    features: (item.features || []) as FeatureBadge[],
    tenantId: item.microsoftTenantId || undefined,
    clientId: item.clientId || undefined,
    organization: item.organization || undefined,
    configuredBy: item.configuredBy || undefined,
    graphAccessVerified: item.status === 'connected',
    connectionId: item.connectionId,
  };
}

const IdentityProviderSection: React.FC<IdentityProviderSectionProps> = ({
  addModalOpen,
  onOpenAddModal,
  onCloseAddModal,
  showHeader = true,
}) => {
  const t = useAccessTheme();
  const { showToast } = useToast();
  const [providers, setProviders] = useState<IdentityProviderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [setupKind, setSetupKind] = useState<IdentityProviderKind | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [providerToDisconnect, setProviderToDisconnect] = useState<IdentityProviderRecord | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const entraProvider = providers.find((provider) => provider.kind === 'entra') || null;

  const refreshProviders = useCallback(async () => {
    try {
      const listed = await listIdentityProviders();
      setProviders(listed.map(mapListedProvider));
    } catch (error) {
      console.error('Failed to load identity providers:', error);
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to load identity providers',
      });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void refreshProviders();
  }, [refreshProviders]);

  const openSetup = (kind: IdentityProviderKind) => {
    onCloseAddModal();
    setSetupKind(kind);
  };

  const handleSyncNow = async (provider: IdentityProviderRecord) => {
    if (provider.kind !== 'entra' || !provider.connectionId) {
      showToast({ type: 'info', message: DEMO_SETUP_MESSAGE });
      return;
    }
    if (syncingId) return;
    setSyncingId(provider.connectionId);
    try {
      const result = await syncEntraDirectory(provider.connectionId);
      showToast({ type: 'success', message: result.message });
      await refreshProviders();
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Directory sync failed',
      });
    } finally {
      setSyncingId(null);
    }
  };

  const requestDisconnect = (provider: IdentityProviderRecord) => {
    if (provider.kind !== 'entra' || !provider.connectionId) {
      showToast({ type: 'info', message: DEMO_SETUP_MESSAGE });
      return;
    }
    if (disconnectingId) return;
    setProviderToDisconnect(provider);
  };

  const handleDisconnect = async () => {
    const provider = providerToDisconnect;
    if (!provider?.connectionId) return;
    setDisconnectingId(provider.connectionId);
    setProviderToDisconnect(null);
    try {
      const result = await disconnectEntraDirectory(provider.connectionId);
      showToast({ type: 'success', message: result.message });
      await refreshProviders();
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to disconnect identity provider',
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <section className="space-y-3">
      {showHeader ? (
        <div>
          <h3 className={`text-base font-semibold ${t.title}`}>Identity Providers</h3>
          <p className={`text-sm mt-1 ${t.muted}`}>
            Connect and manage enterprise directories and authentication providers.
          </p>
        </div>
      ) : null}

      {loading ? (
        <div className={`rounded-xl border px-6 py-10 text-center ${t.card}`}>
          <p className={`text-sm ${t.muted}`}>Loading identity providers...</p>
        </div>
      ) : providers.length === 0 ? (
        <div className={`rounded-xl border px-6 py-10 text-center ${t.card}`}>
          <p className={`text-sm font-medium ${t.title}`}>No identity providers connected</p>
          <p className={`text-sm mt-2 max-w-md mx-auto ${t.muted}`}>
            Connect Microsoft Entra ID, Active Directory, Okta, or another identity provider.
          </p>
          <button
            type="button"
            onClick={onOpenAddModal}
            className={`mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg shadow-sm transition-colors ${t.btnPrimary}`}
          >
            <Plus className="w-4 h-4" /> Add Identity Provider
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map((provider) => (
            <IdentityProviderCard
              key={provider.id}
              provider={provider}
              onManage={() => openSetup(provider.kind)}
              onSyncNow={() => void handleSyncNow(provider)}
              onDisconnect={() => requestDisconnect(provider)}
              syncing={syncingId === provider.connectionId}
              disconnecting={disconnectingId === provider.connectionId}
            />
          ))}
        </div>
      )}

      {addModalOpen ? (
        <AddIdentityProviderModal onClose={onCloseAddModal} onSelect={openSetup} />
      ) : null}

      {setupKind === 'entra' ? (
        <EntraSetupModal
          existing={entraProvider}
          onClose={() => setSetupKind(null)}
          onFinish={(provider) => {
            setProviders((prev) => [...prev.filter((item) => item.kind !== 'entra'), provider]);
            void refreshProviders();
          }}
        />
      ) : null}
      {setupKind === 'active-directory' ? (
        <ActiveDirectorySetupModal onClose={() => setSetupKind(null)} />
      ) : null}
      {setupKind === 'okta' ? <OktaSetupModal onClose={() => setSetupKind(null)} /> : null}
      {setupKind === 'oidc' ? <OidcSetupModal onClose={() => setSetupKind(null)} /> : null}
      {setupKind === 'saml' ? <SamlSetupModal onClose={() => setSetupKind(null)} /> : null}
      {setupKind === 'scim' ? <ScimSetupModal onClose={() => setSetupKind(null)} /> : null}

      {providerToDisconnect ? (
        <ConfirmDialog
          title="Disconnect identity provider?"
          message={
            'Disconnecting "' +
            providerToDisconnect.name +
            '" will permanently remove ' +
            (providerToDisconnect.userCount ?? 0) +
            ' synced user(s) and ' +
            (providerToDisconnect.groupCount ?? 0) +
            ' synced role(s), plus this connection, SSO, and sync configuration. This cannot be undone.'
          }
          onYes={() => void handleDisconnect()}
          onNo={() => setProviderToDisconnect(null)}
          onCancel={() => setProviderToDisconnect(null)}
        />
      ) : null}
    </section>
  );
};

export default IdentityProviderSection;
